import React, { useCallback, useState } from 'react';
import { connect } from 'react-redux';
import { translator, selectors as i18nSelectors } from 'mage-i18n';
import { ProgressBar, SpacerVertical, Typography } from '@klarna/bubble-ui';
import { Alert } from 'moet-core';

import { getDocumentsOfType } from '@/selectors/documentUpload';
import { AttachmentType, AttachmentTypeEnum } from '@/constants/documents';
import { logEvent } from '@/utils/analytics';

import { ACCEPTED_FORMATS, MAX_FILE_SIZE_IN_MB, MAX_NUMBER_OF_FILES, tPrefix } from './constants';
import { ANALYTICS_EVENTS } from './analyticsEvents';
import { LoaderContainer, LoaderTitle } from './styledComponents';
import { UploadingError } from './UploadingError';
import { UploadArea } from './UploadArea';

type UploaderBaseProps = {
  type: AttachmentTypeEnum;
  onUploadDocument: (
    form: FormData,
    options: { onUploadProgress: (event) => void },
  ) => Promise<void>;
  cyElementId: string;
  errors: string[] | null;
  setErrors: (errorIds: string[] | null) => void;
};

type ConnectUploaderProps = {
  documentsOfType: StakeholderDocument[];
  locale: string;
  t: TranslatorFunction;
};

export type EnhancedUploaderProps = UploaderBaseProps & ConnectUploaderProps;

const Uploader = ({
  t,
  locale,
  documentsOfType,
  type = AttachmentType.SUPPORTING_DOCUMENT,
  onUploadDocument,
  cyElementId,
  errors,
  setErrors,
}: EnhancedUploaderProps) => {
  const [loading, setLoading] = useState(false);
  const [uploadingError, setUploadingError] = useState<number | Error | null>(null);
  const [percentage, setPercentage] = useState(0);
  const [uploadingFiles, setUploadingFiles] = useState<File[]>([]);

  const upload = useCallback(
    (submittedFiles: File[]) => {
      const doUpload = async (file: File) => {
        const form = new FormData();
        form.append('file', file);

        setLoading(true);
        setUploadingError(null);

        if (file.size > MAX_FILE_SIZE_IN_MB * 1024 * 1024) {
          setLoading(false);
          setUploadingError(413);
        } else {
          try {
            await onUploadDocument(form, {
              onUploadProgress: event => {
                const percentage = Math.round((event.loaded * 100) / event.total);
                setPercentage(percentage);
              },
            });

            logEvent(ANALYTICS_EVENTS.uploadDocument, { documentType: type });
          } catch (e) {
            // API returns 413 if the file is too large
            if (e?.response?.status === 413) {
              setUploadingError(413);
            } else {
              setUploadingError(e);
            }
          } finally {
            setLoading(false);
          }
        }
      };

      submittedFiles.forEach(doUpload);
    },
    [onUploadDocument],
  );

  const onUpload = useCallback(
    (filesToUpload: File[]) => {
      setUploadingFiles(filesToUpload);
      if (filesToUpload.length > 0) {
        upload(filesToUpload);
      }
    },
    [upload],
  );

  const reUpload = useCallback(() => {
    return upload(uploadingFiles);
  }, [uploadingFiles, upload]);

  const errorProps = { uploadingFiles, reUpload, t, uploadingError, loading };

  const UploadingProgress = () => {
    const localizedPercentage = Intl.NumberFormat(locale, {
      style: 'percent',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(percentage / 100);

    return (
      <>
        {uploadingFiles?.map(file => (
          <LoaderContainer key={file.lastModified}>
            <LoaderTitle>
              <Typography textToken="text-style/text/paragraphs/default/regular" numberOfLines={1}>
                {file.name}
              </Typography>
              <Typography textToken="text-style/text/labels/body/regular">
                {localizedPercentage}
              </Typography>
            </LoaderTitle>
            <SpacerVertical spaceToken="space/100" />
            <ProgressBar percentage={percentage} />
          </LoaderContainer>
        ))}
      </>
    );
  };

  return (
    <>
      <UploadArea
        cyElementId={cyElementId}
        documents={documentsOfType}
        errors={errors}
        onUpload={onUpload}
        setErrors={setErrors}
        t={t}
      />
      <SpacerVertical spaceToken="space/100" />
      {errors && errors.length > 0 && (
        <>
          {/* @ts-expect-error: FIXME */}
          <Alert
            type="error"
            {...{
              [errors.length > 1 ? 'listItems' : 'content']: errors.map(error =>
                t(`${tPrefix}.errors.${error}`, {
                  number: `${MAX_NUMBER_OF_FILES}`,
                  acceptedFileFormats: ACCEPTED_FORMATS.join(' '),
                }),
              ),
            }}
          />
          <SpacerVertical spaceToken="space/100" />
        </>
      )}
      {loading && <UploadingProgress />}
      {uploadingError && <UploadingError {...errorProps} />}
    </>
  );
};

export default connect<ConnectUploaderProps, Record<string, any>, UploaderBaseProps>(
  (state, { type }) => {
    return {
      documentsOfType: getDocumentsOfType(type)(state as GlobalState),
      locale: i18nSelectors.locale(state),
      t: translator(state),
    };
  },
)(Uploader);
